import {
    Checkbox,
    CheckboxProps,
    FormControl,
    FormControlLabel,
    FormControlLabelProps,
    FormHelperText,
    Typography,
} from "@mui/material";
import { cls } from "itsf-ui-common";
import type { FieldError, UseFormMethods } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyle } from "./style";

interface IProps extends Omit<CheckboxProps, "onChange"> {
    control: UseFormMethods["control"];
    error?: FieldError | undefined;
    isRequired: boolean;
    label: FormControlLabelProps["label"];
    name: string;
    fullWidth?: boolean;
}

export const CheckboxWrapper = (props: IProps) => {
    const { label, control, name, error, isRequired, color = "secondary", fullWidth, ...checkBoxProps } = props;
    const { t } = useTranslation("validation");
    const { classes } = useStyle();

    return (
        <Controller
            control={control}
            name={name}
            render={(controllerProps) => {
                const isErrorUnchecked = Boolean(error) && !controllerProps.value;
                const isUnchecked = !controllerProps.value;

                return (
                    <FormControl error={Boolean(error)} fullWidth={fullWidth}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                                    {...checkBoxProps}
                                    checked={controllerProps.value}
                                    className={cls(
                                        checkBoxProps.className,
                                        isErrorUnchecked ? classes.uncheckedError : undefined,
                                        !isErrorUnchecked && isUnchecked ? classes.uncheckedSecondary : undefined
                                    )}
                                    color={isErrorUnchecked ? "error" : color}
                                    inputRef={controllerProps.ref}
                                    name={controllerProps.name}
                                    onChange={(e) => controllerProps.onChange(e.target.checked)}
                                />
                            }
                            label={
                                <Typography color="secondary" variant="small">
                                    {label}
                                </Typography>
                            }
                            sx={{ marginLeft: 0 }}
                        />
                        {isErrorUnchecked && (
                            <FormHelperText id={`error-text-${name}`}>{error?.message ?? t("required")}</FormHelperText>
                        )}
                    </FormControl>
                );
            }}
            rules={{
                required: isRequired && t("required"),
            }}
        />
    );
};
